/*
 * Copyright 2026 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import type { DynamicCrop, DynamicCropPosition } from '../types';

const DEFAULT_DYNAMIC_CROP_ASPECT_RATIO = '9:16';
const DEFAULT_CROP_ASPECT_RATIO = 9 / 16;
const DEFAULT_SOURCE_ASPECT_RATIO = 16 / 9;
const MIN_CROP_FRACTION = 0.01;
const ASPECT_RATIO_PRECISION = 4;

interface SourceDimensions {
  sourceWidth?: number | null;
  sourceHeight?: number | null;
}

interface DynamicCropGeometry {
  cropHeight: number;
  cropAspectRatio: number;
  cropWidthFraction: number;
  maxXPosition: number;
  maxYPosition: number;
  yPosition: number;
  sourceAspectRatio: number;
}

interface ResolveDynamicCropOptions extends SourceDimensions {
  positions?: DynamicCropPosition[];
}

interface DynamicCropMediaPositionProps {
  width: number;
  height: number;
  offsetX: number;
  offsetY: number;
  scale: number;
  focalX: number;
  focalY: number;
}

interface MediaResource extends SourceDimensions {
  width?: number | null;
  height?: number | null;
}

interface DefaultDynamicCropOptions extends SourceDimensions {
  aspectRatio?: string;
  height?: number;
  timestamp?: number;
  xPositionPercent?: number;
  y?: number;
}

interface SetDynamicCropXPositionOptions extends SourceDimensions {
  timestamp?: number;
}

interface AddDynamicCropPositionsOptions extends SourceDimensions {
  timestampTolerance?: number;
}

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

export const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

export const calculateSinusoidalEaseInOut = (t: number) =>
  0.5 * (1 - Math.cos(t * Math.PI));

const getSourceAspectRatio = ({
  sourceWidth,
  sourceHeight,
}: SourceDimensions) =>
  isFiniteNumber(sourceWidth) &&
  isFiniteNumber(sourceHeight) &&
  sourceHeight > 0
    ? sourceWidth / sourceHeight
    : DEFAULT_SOURCE_ASPECT_RATIO;

const formatAspectRatioDimension = (value: number) =>
  Number(value.toFixed(ASPECT_RATIO_PRECISION)).toString();

export function createDynamicCropAspectRatio(
  width?: number | null,
  height?: number | null
) {
  if (
    !isFiniteNumber(width) ||
    !isFiniteNumber(height) ||
    width <= 0 ||
    height <= 0
  ) {
    return DEFAULT_DYNAMIC_CROP_ASPECT_RATIO;
  }

  return `${formatAspectRatioDimension(width)}:${formatAspectRatioDimension(
    height
  )}`;
}

function parseDynamicCropAspectRatio(aspectRatio?: string | null) {
  if (typeof aspectRatio !== 'string') {
    return null;
  }

  const parts = aspectRatio.split(':');
  if (parts.length !== 2) {
    return null;
  }

  const [width, height] = parts.map(Number);
  if (
    !isFiniteNumber(width) ||
    !isFiniteNumber(height) ||
    width <= 0 ||
    height <= 0
  ) {
    return null;
  }

  return width / height;
}

function getParsedDynamicCropAspectRatio(aspectRatio?: string | null) {
  return parseDynamicCropAspectRatio(aspectRatio) ?? DEFAULT_CROP_ASPECT_RATIO;
}

const getNormalizedDynamicCropAspectRatio = (aspectRatio?: string | null) =>
  parseDynamicCropAspectRatio(aspectRatio)
    ? aspectRatio || DEFAULT_DYNAMIC_CROP_ASPECT_RATIO
    : DEFAULT_DYNAMIC_CROP_ASPECT_RATIO;

const getMaxCropHeight = (
  cropAspectRatio: number,
  sourceAspectRatio: number
) =>
  clamp(
    Math.min(1, sourceAspectRatio / cropAspectRatio),
    MIN_CROP_FRACTION,
    1
  );

export function getDynamicCropGeometry(
  dynamicCrop?: DynamicCrop | null,
  sourceWidth?: number | null,
  sourceHeight?: number | null
): DynamicCropGeometry {
  const sourceAspectRatio = getSourceAspectRatio({ sourceWidth, sourceHeight });
  const cropAspectRatio = getParsedDynamicCropAspectRatio(
    dynamicCrop?.aspectRatio
  );
  const maxCropHeight = getMaxCropHeight(cropAspectRatio, sourceAspectRatio);
  const cropHeight = clamp(
    isFiniteNumber(dynamicCrop?.height) ? dynamicCrop.height : maxCropHeight,
    MIN_CROP_FRACTION,
    maxCropHeight
  );
  const cropWidthFraction = clamp(
    (cropHeight * cropAspectRatio) / sourceAspectRatio,
    MIN_CROP_FRACTION,
    1
  );
  const maxXPosition = Math.max(1 - cropWidthFraction, 0);
  const maxYPosition = Math.max(1 - cropHeight, 0);
  const yPosition = clamp(
    isFiniteNumber(dynamicCrop?.y) ? dynamicCrop.y : maxYPosition / 2,
    0,
    maxYPosition
  );

  return {
    cropHeight,
    cropAspectRatio,
    cropWidthFraction,
    maxXPosition,
    maxYPosition,
    yPosition,
    sourceAspectRatio,
  };
}

export function normalizeDynamicCropPositions(
  dynamicCrop?: DynamicCrop | null
): DynamicCropPosition[] {
  const positions = Array.isArray(dynamicCrop?.positions)
    ? dynamicCrop.positions
    : [];

  return positions
    .filter(
      ({ timestamp, xPositionPercent }) =>
        isFiniteNumber(timestamp) && isFiniteNumber(xPositionPercent)
    )
    .map((position) => ({
      ...position,
    }))
    .sort((a, b) => a.timestamp - b.timestamp);
}

export function resolveDynamicCropPositionAtTime(
  dynamicCrop: DynamicCrop | null | undefined,
  currentTime: number,
  options: ResolveDynamicCropOptions = {}
): DynamicCropPosition | null {
  const positions =
    options.positions || normalizeDynamicCropPositions(dynamicCrop);

  if (!positions.length) {
    return null;
  }

  const time = isFiniteNumber(currentTime) ? currentTime : 0;
  const { maxXPosition, maxYPosition, yPosition } = getDynamicCropGeometry(
    dynamicCrop,
    options.sourceWidth,
    options.sourceHeight
  );
  const clampPosition = (
    position: DynamicCropPosition
  ): DynamicCropPosition => ({
    ...position,
    xPositionPercent: clamp(position.xPositionPercent, 0, maxXPosition),
    y: clamp(
      isFiniteNumber(position.y) ? position.y : yPosition,
      0,
      maxYPosition
    ),
  });

  if (time <= positions[0].timestamp) {
    return clampPosition(positions[0]);
  }

  const lastPosition = positions[positions.length - 1];
  if (time >= lastPosition.timestamp) {
    return clampPosition(lastPosition);
  }

  let low = 0;
  let high = positions.length;
  while (low < high) {
    const middle = Math.floor((low + high) / 2);
    const position = positions[middle];

    if (position.timestamp < time) {
      low = middle + 1;
    } else {
      high = middle;
    }
  }

  const nextPositionIndex = low;
  const exactPosition = positions[nextPositionIndex];
  if (exactPosition?.timestamp === time) {
    return clampPosition(exactPosition);
  }

  const current = positions[nextPositionIndex - 1];
  const next = positions[nextPositionIndex];

  const currentYPosition = isFiniteNumber(current.y) ? current.y : yPosition;
  const nextYPosition = isFiniteNumber(next.y) ? next.y : yPosition;

  if (
    next.isPausedMovement ||
    (current.xPositionPercent === next.xPositionPercent &&
      currentYPosition === nextYPosition) ||
    next.timestamp === current.timestamp
  ) {
    return clampPosition({
      ...current,
      timestamp: time,
      isUserEdited: false,
    });
  }

  const progress =
    (time - current.timestamp) / (next.timestamp - current.timestamp);
  const easedProgress = calculateSinusoidalEaseInOut(progress);

  return clampPosition({
    timestamp: time,
    xPositionPercent:
      current.xPositionPercent +
      (next.xPositionPercent - current.xPositionPercent) * easedProgress,
    y: currentYPosition + (nextYPosition - currentYPosition) * easedProgress,
    isPausedMovement: false,
    isUserEdited: false,
  });
}

export function getDynamicCropMediaSizePositionProps(
  resource: MediaResource | null | undefined,
  width: number,
  height: number,
  dynamicCrop: DynamicCrop | null | undefined,
  currentTime: number,
  positions?: DynamicCropPosition[]
): DynamicCropMediaPositionProps | null {
  if (
    !isFiniteNumber(width) ||
    !isFiniteNumber(height) ||
    width <= 0 ||
    height <= 0
  ) {
    return null;
  }

  const sourceWidth = resource?.width ?? resource?.sourceWidth;
  const sourceHeight = resource?.height ?? resource?.sourceHeight;
  const position = resolveDynamicCropPositionAtTime(dynamicCrop, currentTime, {
    sourceWidth,
    sourceHeight,
    positions,
  });

  if (!position) {
    return null;
  }

  const { cropHeight, cropWidthFraction, yPosition } = getDynamicCropGeometry(
    dynamicCrop,
    sourceWidth,
    sourceHeight
  );
  const cropYPosition = isFiniteNumber(position.y) ? position.y : yPosition;
  const mediaWidth = width / cropWidthFraction;
  const mediaHeight = height / cropHeight;

  return {
    width: mediaWidth,
    height: mediaHeight,
    offsetX: position.xPositionPercent * mediaWidth,
    offsetY: cropYPosition * mediaHeight,
    scale: 100,
    focalX: 50,
    focalY: 50,
  };
}

export function createDefaultDynamicCrop(
  options: DefaultDynamicCropOptions = {}
): DynamicCrop {
  const aspectRatio = getNormalizedDynamicCropAspectRatio(options.aspectRatio);
  const sourceAspectRatio = getSourceAspectRatio(options);
  const cropAspectRatio = getParsedDynamicCropAspectRatio(aspectRatio);
  const maxCropHeight = getMaxCropHeight(cropAspectRatio, sourceAspectRatio);
  const cropHeight = clamp(
    isFiniteNumber(options.height) ? options.height : maxCropHeight,
    MIN_CROP_FRACTION,
    maxCropHeight
  );
  const maxYPosition = Math.max(1 - cropHeight, 0);
  const yPosition = clamp(
    isFiniteNumber(options.y) ? options.y : maxYPosition / 2,
    0,
    maxYPosition
  );
  const baseDynamicCrop: DynamicCrop = {
    aspectRatio,
    height: cropHeight,
    y: yPosition,
    positions: [],
  };
  const { maxXPosition } = getDynamicCropGeometry(
    baseDynamicCrop,
    options.sourceWidth,
    options.sourceHeight
  );
  const xPositionPercent = clamp(
    isFiniteNumber(options.xPositionPercent)
      ? options.xPositionPercent
      : maxXPosition / 2,
    0,
    maxXPosition
  );

  return {
    ...baseDynamicCrop,
    positions: [
      {
        timestamp: isFiniteNumber(options.timestamp) ? options.timestamp : 0,
        xPositionPercent,
        y: yPosition,
        isPausedMovement: false,
        isUserEdited: true,
      },
    ],
  };
}

export function setDynamicCropXPosition(
  dynamicCrop: DynamicCrop | null | undefined,
  xPositionPercent: number,
  options: SetDynamicCropXPositionOptions = {}
): DynamicCrop {
  const baseDynamicCrop =
    dynamicCrop ||
    createDefaultDynamicCrop({
      sourceWidth: options.sourceWidth,
      sourceHeight: options.sourceHeight,
      timestamp: options.timestamp,
    });
  const positions = normalizeDynamicCropPositions(baseDynamicCrop);
  const firstPosition =
    positions[0] ||
    createDefaultDynamicCrop({
      aspectRatio: baseDynamicCrop.aspectRatio,
      sourceWidth: options.sourceWidth,
      sourceHeight: options.sourceHeight,
      timestamp: options.timestamp,
    }).positions[0];
  const baseGeometry = getDynamicCropGeometry(
    baseDynamicCrop,
    options.sourceWidth,
    options.sourceHeight
  );
  const { maxXPosition } = baseGeometry;
  const nextFirstPosition = {
    ...firstPosition,
    timestamp: firstPosition.timestamp ?? options.timestamp ?? 0,
    xPositionPercent: clamp(xPositionPercent, 0, maxXPosition),
    y: isFiniteNumber(firstPosition.y)
      ? firstPosition.y
      : baseGeometry.yPosition,
    isUserEdited: true,
  };

  return {
    aspectRatio: getNormalizedDynamicCropAspectRatio(
      baseDynamicCrop.aspectRatio
    ),
    height: baseGeometry.cropHeight,
    y: baseGeometry.yPosition,
    positions: [nextFirstPosition, ...positions.slice(1)],
  };
}

export function addDynamicCropPositions(
  dynamicCrop: DynamicCrop | null | undefined,
  newPositions: DynamicCropPosition[],
  options: AddDynamicCropPositionsOptions = {}
): DynamicCrop {
  const baseDynamicCrop =
    dynamicCrop ||
    createDefaultDynamicCrop({
      sourceWidth: options.sourceWidth,
      sourceHeight: options.sourceHeight,
    });
  const baseGeometry = getDynamicCropGeometry(
    baseDynamicCrop,
    options.sourceWidth,
    options.sourceHeight
  );
  const { maxXPosition, maxYPosition } = baseGeometry;
  const timestampTolerance = isFiniteNumber(options.timestampTolerance)
    ? Math.max(options.timestampTolerance, 0)
    : 0.01;
  const incomingPositions = newPositions
    .filter(
      ({ timestamp, xPositionPercent }) =>
        isFiniteNumber(timestamp) && isFiniteNumber(xPositionPercent)
    )
    .map((position) => ({
      ...position,
      xPositionPercent: clamp(position.xPositionPercent, 0, maxXPosition),
      ...(isFiniteNumber(position.y)
        ? { y: clamp(position.y, 0, maxYPosition) }
        : {}),
    }));

  const positions = incomingPositions.reduce(
    (currentPositions, position) => [
      ...currentPositions.filter(
        ({ timestamp }) =>
          Math.abs(timestamp - position.timestamp) >= timestampTolerance
      ),
      position,
    ],
    normalizeDynamicCropPositions(baseDynamicCrop)
  );

  return {
    aspectRatio: getNormalizedDynamicCropAspectRatio(
      baseDynamicCrop.aspectRatio
    ),
    height: baseGeometry.cropHeight,
    y: baseGeometry.yPosition,
    positions: positions.sort((a, b) => a.timestamp - b.timestamp),
  };
}
